import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { API_BASE } from "../lib/api";
import PageHeader from "../components/PageHeader";
import WhatsAppSettings from "./WhatsAppSettings";
import PricingSettings from "./PricingSettings";
import VoiceConnections from "./VoiceConnections";
import { toast } from "sonner";
import { Building2, MessageSquare, Bot, ShieldCheck, Save, ArrowRight, FileText, Plus, Trash2, Sparkles, Upload, Loader2, RefreshCw, UploadCloud, Store, ExternalLink, Bell, ShieldAlert, IndianRupee, Workflow, KeyRound, Copy, Phone } from "lucide-react";

const TABS = [
  { k: "business", label: "Business", icon: Building2 },
  { k: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { k: "wa-profile", label: "WhatsApp Profile", icon: Store },
  { k: "templates", label: "Templates", icon: FileText },
  { k: "pricing", label: "Pricing", icon: IndianRupee },
  { k: "notifications", label: "Notifications", icon: Bell },
  { k: "ai", label: "AI", icon: Bot },
  { k: "voice", label: "Voice & Calling", icon: Phone },
  { k: "pipeline", label: "Pipeline", icon: Workflow },
  { k: "security", label: "Security", icon: ShieldCheck },
];

const WA_VERTICALS = ["OTHER", "AUTO", "BEAUTY", "APPAREL", "EDU", "ENTERTAIN", "EVENT_PLAN",
  "FINANCE", "GROCERY", "GOVT", "HOTEL", "HEALTH", "NONPROFIT", "PROF_SERVICES", "RETAIL",
  "TRAVEL", "RESTAURANT", "NOT_A_BIZ"];

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";

const PROFILE_FIELDS = [
  { k: "business_name", label: "Business Name", type: "input" },
  { k: "website", label: "Website", type: "input" },
  { k: "email", label: "Email", type: "input" },
  { k: "phone", label: "Phone", type: "input" },
  { k: "business_hours", label: "Business Hours", type: "input" },
  { k: "description", label: "Description", type: "textarea" },
  { k: "services", label: "Services", type: "textarea" },
  { k: "products", label: "Products", type: "textarea" },
  { k: "policies", label: "Policies", type: "textarea" },
];

function BusinessTab() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef();
  useEffect(() => { api.get("/business-profile").then((r) => setProfile(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      PROFILE_FIELDS.forEach((f) => (payload[f.k] = profile[f.k] || ""));
      await api.put("/business-profile", payload);
      toast.success("Business profile saved");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const aiFill = async () => {
    setSuggesting(true);
    try {
      const r = await api.post("/business-profile/ai-suggest");
      const s = r.data || {};
      setProfile((p) => ({ ...p, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v)) }));
      toast.success("AI filled fields from your knowledge base & chats — review & save");
    } catch (e) { toast.error(e.response?.data?.detail || "AI suggestion failed"); }
    finally { setSuggesting(false); }
  };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/business-profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, logo_path: r.data.logo_path }));
      if (r.data.whatsapp_synced) {
        toast.success("Logo uploaded & synced to your WhatsApp profile picture");
      } else if (r.data.whatsapp_error && r.data.whatsapp_error !== "not_connected") {
        const permErr = /permission/i.test(r.data.whatsapp_error);
        toast.warning("Logo saved to your dashboard", {
          description: permErr
            ? "WhatsApp did not accept the new profile picture (permissions). Your connected token can send messages and edit profile text, but changing the display picture needs App-level access — add your system user as an admin of the Meta App, or generate a token with whatsapp_business_management + app access, then re-upload."
            : "WhatsApp profile picture update failed: " + r.data.whatsapp_error,
        });
      } else {
        toast.success("Logo uploaded");
      }
    } catch (err) { toast.error("Logo upload failed"); }
    finally { setUploading(false); if (logoRef.current) logoRef.current.value = ""; }
  };
  if (!profile) return <p className="text-sm text-[#8696A0]">Loading…</p>;
  return (
    <div className="border border-[#E9EDEF] bg-white rounded-lg" data-testid="business-profile-form">
      <div className="px-5 py-4 border-b border-[#E9EDEF] flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-heading text-sm font-medium text-[#111B21]">MuviArts Business Profile</h3>
        <div className="flex items-center gap-2">
          <button data-testid="bp-ai-suggest" onClick={aiFill} disabled={suggesting}
            className="flex items-center gap-2 bg-[#E7FCE8] text-[#128C7E] border border-[#25D366]/30 hover:bg-[#DCF8C6] rounded-lg px-3 py-2 text-sm transition-colors disabled:opacity-50">
            {suggesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Auto-fill
          </button>
          <button data-testid="save-business-profile" onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div className="p-5 flex items-center gap-4 border-b border-[#E9EDEF]">
        <div className="h-16 w-16 rounded-lg border border-[#E9EDEF] bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
          {profile.logo_path ? <img src={`${API_BASE}/assets/file/${profile.logo_path}`} alt="logo" className="w-full h-full object-cover" /> : <Building2 className="h-6 w-6 text-[#8696A0]" />}
        </div>
        <div>
          <p className="text-sm text-[#111B21]">Business Logo</p>
          <p className="text-xs text-[#8696A0] mb-2">Uploading also sets your WhatsApp business profile picture (what customers see) when WhatsApp is connected.</p>
          <input ref={logoRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-input" />
          <button data-testid="logo-upload" onClick={() => logoRef.current?.click()} disabled={uploading}
            className="flex items-center gap-2 border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 py-1.5 text-xs transition-colors disabled:opacity-50">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />} Upload Logo
          </button>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {PROFILE_FIELDS.map((f) => (
          <div key={f.k} className={f.type === "textarea" ? "md:col-span-2" : ""}>
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea data-testid={`bp-${f.k}`} rows={2} value={profile[f.k] || ""} onChange={(e) => setProfile({ ...profile, [f.k]: e.target.value })} className={inputCls + " mt-1"} />
            ) : (
              <input data-testid={`bp-${f.k}`} value={profile[f.k] || ""} onChange={(e) => setProfile({ ...profile, [f.k]: e.target.value })} className={inputCls + " mt-1"} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const { user, checkAuth } = useAuth();
  const hasPassword = user?.has_password !== false;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const body = { new_password: next };
      if (hasPassword) body.current_password = current;
      const r = await api.post("/auth/change-password", body);
      toast.success(r.data?.was_set ? "Password set — you can now sign in with email & password" : "Password updated");
      setCurrent(""); setNext(""); setConfirm("");
      if (checkAuth) checkAuth();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4 max-w-md" data-testid="change-password-card">
      <div>
        <h3 className="font-heading text-sm font-medium text-[#111B21] flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#075E54]" /> {hasPassword ? "Change Password" : "Set a Password"}
        </h3>
        <p className="text-xs text-[#8696A0] mt-1">
          {hasPassword
            ? "Update the password for your email login."
            : "You signed in with Google. Set a password to also log in with your email."}
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3">
        {hasPassword && (
          <input data-testid="current-password" type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
            placeholder="Current password" required className={inputCls} />
        )}
        <input data-testid="new-password" type="password" value={next} onChange={(e) => setNext(e.target.value)}
          placeholder="New password (min 6 chars)" required className={inputCls} />
        <input data-testid="confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password" required className={inputCls} />
        <button data-testid="change-password-submit" type="submit" disabled={busy}
          className="inline-flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {hasPassword ? "Update Password" : "Set Password"}
        </button>
      </form>
    </div>
  );
}

function PipelineTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [nums, setNums] = useState([]);
  const [newNum, setNewNum] = useState("");
  const [savingNums, setSavingNums] = useState(false);
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [keyName, setKeyName] = useState("");
  const [report, setReport] = useState(null);
  const [runningDry, setRunningDry] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get("/lead-settings").then((r) => setNums(r.data.internal_numbers || [])).catch(() => {});
    api.get("/api-keys").then((r) => setKeys(r.data || [])).catch(() => {});
  }, []);

  const saveNums = async (list) => {
    setSavingNums(true);
    try {
      const r = await api.put("/lead-settings", { internal_numbers: list });
      setNums(r.data.internal_numbers || []);
      toast.success("Internal numbers saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingNums(false); }
  };
  const addNum = () => {
    const n = newNum.trim();
    if (!n) return;
    if (nums.includes(n)) { setNewNum(""); return; }
    const list = [...nums, n];
    setNewNum("");
    saveNums(list);
  };
  const removeNum = (n) => saveNums(nums.filter((x) => x !== n));

  const createKey = async (scope) => {
    try {
      const r = await api.post("/api-keys", scope === "quotes" ? { name: keyName || "JARVIS quote key", scope: "quotes" } : { name: keyName || "Read-only key" });
      setNewKey(r.data);
      setKeyName("");
      setKeys((k) => [{ ...r.data, key: undefined }, ...k]);
      toast.success("API key created — copy it now, it won't be shown again");
    } catch { toast.error("Could not create key"); }
  };
  const revokeKey = async (id) => {
    try { await api.delete(`/api-keys/${id}`); setKeys((k) => k.map((x) => x.key_id === id ? { ...x, revoked: true } : x)); }
    catch { toast.error("Revoke failed"); }
  };

  const runDry = async () => {
    setRunningDry(true);
    try { const r = await api.post("/leads/infer-status", {}); setReport(r.data); }
    catch { toast.error("Dry-run failed"); }
    finally { setRunningDry(false); }
  };
  const applyBackfill = async () => {
    if (!window.confirm("Apply these inferred statuses to all leads? This writes to lead history.")) return;
    setApplying(true);
    try { const r = await api.post("/leads/infer-status", { apply: true }); setReport(r.data); toast.success(`Applied ${r.data.applied} changes`); }
    catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };

  const card = "border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4";
  return (
    <div className="space-y-6" data-testid="pipeline-tab">
      {/* Internal numbers */}
      <div className={card}>
        <div>
          <h3 className="font-heading text-sm font-medium text-[#111B21] flex items-center gap-2"><Phone className="h-4 w-4 text-[#075E54]" /> Internal / Test Numbers</h3>
          <p className="text-xs text-[#8696A0] mt-1">Chats from these numbers are marked <b>Test</b> and excluded from all dashboard counts.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {nums.map((n) => (
            <span key={n} data-testid="internal-number-chip" className="inline-flex items-center gap-1.5 text-xs bg-[#F0F2F5] border border-[#E9EDEF] rounded-full pl-3 pr-2 py-1 font-mono text-[#374151]">
              {n}
              <button data-testid="remove-internal-number" onClick={() => removeNum(n)} className="text-[#8696A0] hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
          {nums.length === 0 && <span className="text-xs text-[#8696A0]">None yet.</span>}
        </div>
        <div className="flex gap-2">
          <input data-testid="internal-number-input" value={newNum} onChange={(e) => setNewNum(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addNum()} placeholder="+9198…"
            className="flex-1 text-sm bg-[#F0F2F5] border border-transparent focus:border-[#25D366] rounded-lg px-3 py-2 focus:outline-none text-[#111B21]" />
          <button data-testid="add-internal-number" onClick={addNum} disabled={savingNums}
            className="inline-flex items-center gap-1.5 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-3 py-2 text-sm disabled:opacity-50"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      {/* Read-only API keys */}
      <div className={card}>
        <div>
          <h3 className="font-heading text-sm font-medium text-[#111B21] flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#075E54]" /> Read-only API Keys</h3>
          <p className="text-xs text-[#8696A0] mt-1">For assistants like JARVIS. Can READ leads, conversations, dashboard & knowledge. Cannot send messages or change data.</p>
        </div>
        {newKey && (
          <div className="bg-[#E7FCE8] border border-[#25D366]/40 rounded-lg p-3" data-testid="new-api-key">
            <p className="text-[11px] text-[#075E54] font-semibold mb-1">Copy this key now — it won't be shown again:</p>
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono break-all text-[#111B21] bg-white rounded px-2 py-1 flex-1">{newKey.key}</code>
              <button onClick={() => { navigator.clipboard.writeText(newKey.key); toast.success("Copied"); }} className="text-[#075E54] hover:text-[#128C7E]"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <input data-testid="api-key-name" value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Key name (e.g. JARVIS)"
            className="flex-1 text-sm bg-[#F0F2F5] border border-transparent focus:border-[#25D366] rounded-lg px-3 py-2 focus:outline-none text-[#111B21]" />
          <button data-testid="create-api-key" onClick={() => createKey()}
            className="inline-flex items-center gap-1.5 bg-[#075E54] text-white hover:bg-[#128C7E] rounded-lg font-medium px-3 py-2 text-sm"><Plus className="h-4 w-4" /> Create key</button>
        </div>
        <div className="flex items-center justify-between gap-2 text-xs border border-dashed border-[#25D366]/50 rounded-lg px-3 py-2">
          <p className="text-[#54656F] min-w-0">JARVIS quote key: reads like the key above and can also send a bulk-bhajan quote that you approved in JARVIS. It cannot send anything else.</p>
          <button data-testid="create-quote-key" onClick={() => createKey("quotes")}
            className="shrink-0 inline-flex items-center gap-1 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-3 py-1.5"><Plus className="h-3.5 w-3.5" /> Quote key</button>
        </div>
        <div className="space-y-1.5">
          {keys.map((k) => (
            <div key={k.key_id} data-testid="api-key-row" className="flex items-center justify-between text-xs border border-[#E9EDEF] rounded-lg px-3 py-2">
              <div className="min-w-0">
                <p className="font-medium text-[#111B21]">{k.name} {k.revoked && <span className="text-red-600">(revoked)</span>}</p>
                <p className="font-mono text-[10px] text-[#8696A0]">{k.key_prefix}… · {k.scope === "quotes" ? "read + approved quotes · " : "read-only · "}{new Date(k.created_at).toLocaleDateString()}</p>
              </div>
              {!k.revoked && <button data-testid="revoke-api-key" onClick={() => revokeKey(k.key_id)} className="text-[#8696A0] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
          {keys.length === 0 && <p className="text-xs text-[#8696A0]">No keys yet.</p>}
        </div>
      </div>

      {/* Backfill */}
      <div className={card}>
        <div>
          <h3 className="font-heading text-sm font-medium text-[#111B21] flex items-center gap-2"><RefreshCw className="h-4 w-4 text-[#075E54]" /> Infer statuses for existing leads</h3>
          <p className="text-xs text-[#8696A0] mt-1">Runs the pipeline rules over every existing lead's full history. Shows a dry-run report first — nothing is written until you click Apply.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="backfill-dryrun" onClick={runDry} disabled={runningDry}
            className="inline-flex items-center gap-1.5 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm disabled:opacity-50">
            {runningDry && <Loader2 className="h-4 w-4 animate-spin" />} Run dry-run
          </button>
          {report && isAdmin && report.dry_run && report.changes > 0 && (
            <button data-testid="backfill-apply" onClick={applyBackfill} disabled={applying}
              className="inline-flex items-center gap-1.5 bg-[#075E54] text-white hover:bg-[#128C7E] rounded-lg font-medium px-4 py-2 text-sm disabled:opacity-50">
              {applying && <Loader2 className="h-4 w-4 animate-spin" />} Apply {report.changes} changes
            </button>
          )}
        </div>
        {report && (
          <div data-testid="backfill-report" className="border border-[#E9EDEF] rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-[#F8FAFC] text-xs text-[#54656F] flex items-center justify-between">
              <span>{report.dry_run ? "Dry-run" : "Applied"} · {report.total_leads_scanned} scanned · {report.changes} proposed{!report.dry_run ? ` · ${report.applied} applied` : ""}</span>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-[#E9EDEF]">
              {report.proposals.length === 0 && <p className="px-3 py-4 text-xs text-[#8696A0] text-center">No changes — every lead already matches the rules.</p>}
              {report.proposals.map((p) => (
                <div key={p.lead_id} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[#111B21] font-medium truncate">{p.name || p.phone}</p>
                    <p className="text-[#8696A0] truncate">{p.reason}{p.quoted_amount ? ` · ₹${p.quoted_amount}` : ""}</p>
                  </div>
                  <span className="shrink-0 text-[#54656F]">{p.current} → <b className="text-[#075E54]">{p.proposed}</b></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SecurityTab() {
  const [diag, setDiag] = useState(null);
  useEffect(() => { api.get("/diagnostics").then((r) => setDiag(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="security-tab">
      <ChangePasswordCard />
      <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3">
        <h3 className="font-heading text-sm font-medium text-[#111B21] flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-green-600" /> Security</h3>
        <ul className="text-xs text-[#54656F] space-y-1.5 font-mono">
          <li>✓ Access token & app secret encrypted at rest (Fernet), never returned to the browser.</li>
          <li>✓ Webhook verify token stored encrypted; revealed only to authenticated admins.</li>
          <li>✓ Incoming webhooks verified via X-Hub-Signature-256 (HMAC-SHA256).</li>
          <li>✓ Duplicate webhook events rejected (idempotency by message id).</li>
          <li>✓ Workspace resolved server-side — client-provided workspace ids are never trusted.</li>
        </ul>
      </div>

      <div className="border border-[#E9EDEF] bg-white rounded-lg">
        <div className="px-5 py-4 border-b border-[#E9EDEF]"><h3 className="font-heading text-sm font-medium text-[#111B21]">Diagnostics — Recent Webhook Events</h3></div>
        <div className="p-5 font-mono text-xs space-y-1 max-h-52 overflow-y-auto">
          {(diag?.webhook_events || []).length === 0 && <p className="text-[#8696A0]">No webhook events yet.</p>}
          {(diag?.webhook_events || []).map((e) => (
            <div key={e.event_key} className="flex justify-between text-[#54656F]">
              <span className="truncate">{e.type} · {e.raw_type} · {e.event_key?.slice(0, 22)}…</span>
              <span className="text-[#8696A0]">{new Date(e.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-[#E9EDEF] bg-white rounded-lg">
        <div className="px-5 py-4 border-b border-[#E9EDEF]"><h3 className="font-heading text-sm font-medium text-[#111B21]">Failed Messages</h3></div>
        <div className="p-5 font-mono text-xs space-y-1 max-h-40 overflow-y-auto">
          {(diag?.failed_messages || []).length === 0 && <p className="text-[#8696A0]">No failures.</p>}
          {(diag?.failed_messages || []).map((m) => (
            <div key={m.message_id} className="text-red-400/80">{m.direction} · {m.content?.slice(0, 40)} · {new Date(m.created_at).toLocaleString()}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [waba, setWaba] = useState(undefined); // undefined=loading, ""/null=missing
  const [form, setForm] = useState({ name: "", language: "en_US", body: "", category: "UTILITY" });
  const load = () => api.get("/templates").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/whatsapp/integration").then((r) => setWaba(r.data?.business_account_id || "")).catch(() => setWaba(""));
  }, []);
  const add = async () => {
    if (!form.name.trim() || !form.body.trim()) { toast.error("Name and body required"); return; }
    try {
      const r = await api.post("/templates", form);
      const st = r.data?.status;
      if (st === "PENDING") toast.success("Submitted to Meta — in approval ⏳");
      else if (st === "APPROVED") toast.success("Template approved ✅");
      else if (st === "ERROR") toast.error(r.data?.rejection_reason || "Meta rejected the submission");
      else toast.success("Template saved");
      setForm({ name: "", language: "en_US", body: "", category: "UTILITY" }); load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/templates/${id}`); load(); toast.success("Deleted"); };
  const [suggesting, setSuggesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const aiSuggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post("/templates/ai-suggest", { hint: form.name || form.body || "", category: form.category });
      setForm({ name: r.data.name || form.name, language: r.data.language || "en_US", body: r.data.body || "", category: r.data.category || "UTILITY" });
      toast.success("AI drafted a template — review & save");
    } catch (e) { toast.error(e.response?.data?.detail || "AI suggestion failed"); }
    finally { setSuggesting(false); }
  };
  const submit = async (id) => {
    try {
      const r = await api.post(`/templates/${id}/submit`);
      if (r.data.ok) toast.success(`Submitted to Meta — ${r.data.status}`);
      else toast.error(r.data.error || "Submission failed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Connect WhatsApp first"); }
  };
  const sync = async () => {
    setSyncing(true);
    try { const r = await api.post("/templates/sync"); toast.success(`Synced ${r.data.updated} template status update(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };
  const [seeding, setSeeding] = useState(false);
  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/templates/seed-defaults", { dashboard_url: window.location.origin });
      toast.success(`Submitted ${r.data.submitted} MuviArts template(s) to Meta. Click "Sync Meta status" in a few minutes.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit templates — connect WhatsApp first"); }
    finally { setSeeding(false); }
  };
  const STATUS_LABEL = { PENDING: "In approval", APPROVED: "Approved", REJECTED: "Rejected", DRAFT: "Draft", ERROR: "Submit error", PAUSED: "Paused", DISABLED: "Disabled" };
  const statusBadge = (s) => {
    const map = {
      APPROVED: "bg-green-500/10 text-green-600 border-green-500/20",
      REJECTED: "bg-red-500/10 text-red-600 border-red-500/20",
      ERROR: "bg-red-500/10 text-red-600 border-red-500/20",
      PENDING: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      PAUSED: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    };
    return map[s] || "bg-[#F0F2F5] text-[#54656F] border-[#CBD5E1]";
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="templates-tab">
      {waba === "" && (
        <div className="lg:col-span-3 border border-amber-300 bg-amber-50 rounded-lg px-5 py-3.5 flex items-start gap-3" data-testid="waba-missing-banner">
          <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800">
            <b>WhatsApp Business Account ID not set.</b> Templates can't be submitted to Meta until you add it.
            Open <b>Settings → WhatsApp</b> connection and paste your WABA ID (find it in Meta <b>WhatsApp Manager → API Setup</b>, shown next to your phone number).
          </div>
        </div>
      )}
      <div className="lg:col-span-3 border border-[#25D366]/30 bg-[#F0FBF3] rounded-lg px-5 py-4 flex items-start gap-3" data-testid="seed-templates-card">
        <Sparkles className="h-4 w-4 text-[#128C7E] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#111B21]">MuviArts lead templates (recommended)</p>
          <p className="text-xs text-[#54656F] mt-1">
            One click creates &amp; submits the 5 lead-oriented follow-up templates used by your
            Cold / Warm / Hot automations (enquiry, next step, scope, close, quote follow-up) plus the
            daily lead-digest template with an <b>Open Dashboard</b> button. Then click
            <b> Sync Meta status</b> in a few minutes to see approvals. No messages are sent to leads.
          </p>
        </div>
        <button data-testid="seed-templates-btn" onClick={seedDefaults} disabled={seeding || waba === ""}
          className="shrink-0 flex items-center gap-1.5 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50">
          {seeding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Create &amp; submit
        </button>
      </div>
      <div className="lg:col-span-2 border border-[#E9EDEF] bg-white rounded-lg">
        <div className="px-5 py-4 border-b border-[#E9EDEF] flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-heading text-sm font-medium text-[#111B21]">WhatsApp Templates</h3>
          <button data-testid="tpl-sync" onClick={sync} disabled={syncing}
            className="flex items-center gap-1.5 border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 py-1.5 text-xs transition-colors disabled:opacity-50">
            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync Meta status
          </button>
        </div>
        <div className="divide-y divide-[#E9EDEF]">
          {rows.length === 0 && <p className="px-5 py-10 text-center text-sm text-[#8696A0]"><FileText className="h-5 w-5 mx-auto mb-2 text-[#54656F]" />No templates yet.</p>}
          {rows.map((t) => (
            <div key={t.template_id} className="px-5 py-3 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[#111B21] font-mono flex items-center gap-2 flex-wrap">{t.name} <span className="text-[#8696A0]">· {t.language} · {t.category}</span>
                  {t.status && <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded-lg border ${statusBadge(t.status)}`}>{STATUS_LABEL[t.status] || t.status}</span>}
                </p>
                <p className="text-xs text-[#8696A0] mt-1">{t.body}</p>
                {(t.status === "REJECTED" || t.status === "ERROR") && t.rejection_reason &&
                  <p className="text-[11px] text-red-500 mt-1">Reason: {t.rejection_reason}</p>}
              </div>
              {["DRAFT", "ERROR", "REJECTED"].includes(t.status) && (
                <button onClick={() => submit(t.template_id)} data-testid={`submit-tpl-${t.template_id}`}
                  className="text-[10px] font-mono border border-[#25D366]/40 text-[#075E54] hover:bg-[#E7FCE8] rounded-lg px-2 py-1 transition-colors">Resubmit</button>
              )}
              <button onClick={() => del(t.template_id)} data-testid={`del-tpl-${t.template_id}`} className="text-[#8696A0] hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3 h-fit">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-sm font-medium text-[#111B21]">New Template</h3>
          <button data-testid="tpl-ai-suggest" onClick={aiSuggest} disabled={suggesting}
            className="flex items-center gap-1.5 bg-[#E7FCE8] text-[#128C7E] border border-[#25D366]/30 hover:bg-[#DCF8C6] rounded-lg px-2.5 py-1.5 text-xs transition-colors disabled:opacity-50">
            {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI Suggest
          </button>
        </div>
        <input data-testid="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="template_name (lowercase_snake_case)" className={inputCls + " font-mono"} />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="tpl-language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} placeholder="en_US" className={inputCls + " font-mono"} />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
            <option value="MARKETING">MARKETING (re-engagement follow-ups)</option>
            <option value="UTILITY">UTILITY (order/account updates)</option>
            <option value="AUTHENTICATION">AUTHENTICATION</option>
          </select>
        </div>
        <textarea data-testid="tpl-body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Hi {{1}}, following up on your MuviArts project — reply here and we'll continue. 🎶" className={inputCls} />
        <button data-testid="tpl-add" onClick={add} className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors">
          <Plus className="h-4 w-4" /> Submit for approval
        </button>
        <div className="text-[11px] text-[#8696A0] space-y-1">
          <p><b>Saving submits it to Meta for approval automatically.</b> Status shows "In approval" until Meta reviews (usually minutes–a few hours), then Approved/Rejected.</p>
          <p>Use <span className="font-mono">{"{{1}}"}</span> for a variable only if your template needs one. Approved templates re-open chats with leads silent over 24 hours. Choose the category by intent: proactive re-engagement / promotional nudges must be <b>MARKETING</b>; only genuine transactional updates (order, account, appointment) qualify as UTILITY. Miscategorising a follow-up as UTILITY is a common rejection reason.</p>
        </div>
      </div>
    </div>
  );
}

function WhatsAppProfileTab() {
  const [p, setP] = useState({ description: "", about: "", address: "", email: "", websites: "", vertical: "OTHER" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [locked, setLocked] = useState(false);
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    api.get("/whatsapp/business-profile").then((r) => {
      const d = r.data || {};
      if (!d.available) {
        setAvailable(false);
        setLocked(!!d.locked);
        setErr(d.reason || d.error || "Could not load WhatsApp profile");
        return;
      }
      const prof = d.profile || {};
      setAvailable(true);
      setP({
        description: prof.description || "", about: prof.about || "", address: prof.address || "",
        email: prof.email || "", websites: (prof.websites || []).join(", "), vertical: prof.vertical || "OTHER",
      });
    }).catch((e) => setErr(e.response?.data?.detail || "Could not load WhatsApp profile")).finally(() => setLoading(false));
  }, []);
  const save = async () => {
    if (!available) return;
    setSaving(true);
    try {
      const r = await api.post("/whatsapp/business-profile", p);
      if (r.data.ok) toast.success("WhatsApp profile updated");
      else toast.error(r.data.error || "Update failed");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-6 max-w-3xl" data-testid="wa-profile-tab">
      <div className="border border-[#E9EDEF] bg-white rounded-lg">
        <div className="px-5 py-4 border-b border-[#E9EDEF] flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2"><Store className="h-4 w-4 text-[#075E54]" />
            <h3 className="font-heading text-sm font-medium text-[#111B21]">Public WhatsApp Profile</h3></div>
          <button data-testid="wap-save" onClick={save} disabled={saving || loading || !available}
            className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save to WhatsApp
          </button>
        </div>
        <div className="p-5">
          <p className="text-xs text-[#8696A0] mb-4">These fields appear on your WhatsApp business profile (the first screen customers see). Saved live to Meta on your connected number.</p>
          {loading ? <p className="text-sm text-[#8696A0]">Loading…</p> : !available ? (
            <div className={`text-sm rounded-lg p-3 border ${locked ? "text-red-600 bg-red-500/10 border-red-500/20" : "text-amber-600 bg-amber-500/10 border-amber-500/20"}`} data-testid="wap-error">
              {locked ? (
                <><b>Blocked / Restricted.</b> {err} Editing is disabled because Meta has locked this WhatsApp Business Account. This cannot be resolved by reconnecting or editing the profile — it requires Meta account review.</>
              ) : (
                <>{err} — check the WhatsApp connection tab.</>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">About (short tagline)</label>
                <input data-testid="wap-about" value={p.about} placeholder="Music production & artist services"
                  onChange={(e) => setP({ ...p, about: e.target.value })} className={inputCls + " mt-1"} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Category (Vertical)</label>
                <select data-testid="wap-vertical" value={p.vertical} onChange={(e) => setP({ ...p, vertical: e.target.value })} className={inputCls + " mt-1"}>
                  {WA_VERTICALS.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Description</label>
                <textarea data-testid="wap-description" rows={2} value={p.description} placeholder="What MuviArts offers…"
                  onChange={(e) => setP({ ...p, description: e.target.value })} className={inputCls + " mt-1"} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Address</label>
                <input data-testid="wap-address" value={p.address} placeholder="City, Country"
                  onChange={(e) => setP({ ...p, address: e.target.value })} className={inputCls + " mt-1"} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Email</label>
                <input data-testid="wap-email" value={p.email} placeholder="hello@muviarts.com"
                  onChange={(e) => setP({ ...p, email: e.target.value })} className={inputCls + " mt-1"} />
              </div>
              <div className="md:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Websites (comma separated, max 2)</label>
                <input data-testid="wap-websites" value={p.websites} placeholder="https://muviarts.com"
                  onChange={(e) => setP({ ...p, websites: e.target.value })} className={inputCls + " mt-1"} />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="border border-[#E9EDEF] bg-white rounded-lg p-5">
        <h4 className="font-heading text-sm text-[#111B21] mb-2">Legal, Business type & Grievance officer</h4>
        <p className="text-xs text-[#8696A0]">The compliance fields (Legal name of business, Business type, Customer care & Grievance officer) are set by Meta, not via the API. Edit them in <span className="text-[#374151]">Meta WhatsApp Manager → Account tools → Business compliance</span>.</p>
        <a href="https://business.facebook.com/wa/manage/" target="_blank" rel="noreferrer" data-testid="wap-meta-link"
          className="inline-flex items-center gap-1.5 mt-3 text-xs text-[#075E54] hover:text-[#128C7E] transition-colors">
          Open Meta WhatsApp Manager <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}

function Toggle({ on, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${on ? "bg-[#25D366]" : "bg-[#CBD5E1]"}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function NotificationsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [digestTpl, setDigestTpl] = useState(null);
  useEffect(() => {
    api.get("/notification-settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/templates").then((r) => {
      const t = (r.data || []).find((x) => x.name === "muviarts_daily_leads_digest_v1");
      setDigestTpl(t || false);
    }).catch(() => setDigestTpl(false));
  }, []);
  if (!s) return <div className="text-sm text-[#8696A0]">Loading…</div>;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/notification-settings", { ...s, dashboard_url: window.location.origin });
      setS(r.data);
      toast.success("Notification settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    if (!(s.whatsapp_number || "").trim()) { toast.error("Enter a WhatsApp number first"); return; }
    setTesting(true);
    try {
      const r = await api.post("/notification-settings/test", { whatsapp_number: s.whatsapp_number });
      if (r.data.ok) toast.success("Test alert sent to " + s.whatsapp_number);
      else toast.warning("Couldn't deliver: " + (r.data.error || "unknown") + ". If that number hasn't messaged your WhatsApp business in the last 24h, send it a quick 'hi' and try again.");
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };
  const RANKS = [
    ["Hot", "🔥 Hot — ready to buy (price / booking / pay)"],
    ["Warm", "Warm — shared 2+ details"],
    ["Cold", "Cold — just started chatting"],
  ];
  return (
    <div className="max-w-xl space-y-5" data-testid="notifications-tab">
      <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="font-heading text-sm text-[#111B21]">Admin Lead Notifications</h4>
            <p className="text-xs text-[#8696A0] mt-1">Get a WhatsApp alert the moment a lead reaches a chosen rank.</p>
          </div>
          <Toggle on={s.enabled} onChange={(v) => setS({ ...s, enabled: v })} testid="notif-enabled-toggle" />
        </div>

        <div className={s.enabled ? "space-y-4" : "space-y-4 opacity-50 pointer-events-none"}>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Notify this WhatsApp number</label>
            <input data-testid="notif-number-input" value={s.whatsapp_number}
              onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })}
              placeholder="e.g. 918055566673 (with country code)" className={inputCls} />
            <p className="text-[10px] text-[#8696A0] mt-1">Digits only, include the country code (e.g. 91 for India).</p>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-[#374151]">Alert me for</p>
            {RANKS.map(([r, label]) => (
              <div key={r} className="flex items-center justify-between border border-[#E9EDEF] rounded-lg px-3 py-2">
                <span className="text-sm text-[#374151]">{label}</span>
                <Toggle on={!!s.ranks?.[r]} onChange={(v) => setS({ ...s, ranks: { ...s.ranks, [r]: v } })} testid={`notif-rank-${r}`} />
              </div>
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Message text</label>
            <textarea data-testid="notif-template-input" value={s.message_template}
              onChange={(e) => setS({ ...s, message_template: e.target.value })} rows={4} className={inputCls} />
            <p className="text-[10px] text-[#8696A0] mt-1">Placeholders: <code>{"{rank}"}</code> <code>{"{name}"}</code> <code>{"{phone}"}</code> <code>{"{link}"}</code> — link opens the chat in your dashboard.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button data-testid="notif-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
          <button data-testid="notif-test" onClick={sendTest} disabled={testing || !s.enabled}
            title={s.enabled ? "Send a test alert to this number" : "Turn notifications on first"}
            className="inline-flex items-center gap-2 bg-white text-[#075E54] border border-[#25D366]/40 hover:bg-[#E7FCE8] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <Bell className="h-4 w-4" /> {testing ? "Sending…" : "Send test"}
          </button>
        </div>
      </div>
      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
        <p className="text-xs text-amber-800"><strong>Heads up:</strong> WhatsApp only delivers free-form alerts if the notified number has messaged your WhatsApp business number within the last 24 hours. If alerts stop arriving, send a quick "hi" from that number to reopen the 24-hour window.</p>
      </div>

      <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4" data-testid="daily-digest-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="font-heading text-sm text-[#111B21]">Daily lead digest</h4>
            <p className="text-xs text-[#8696A0] mt-1">A once-a-day WhatsApp summary at 9:00 AM IST to the number above, using an approved template so it's delivered even outside the 24-hour window.</p>
          </div>
          <Toggle on={!!s.daily_digest_enabled} onChange={(v) => setS({ ...s, daily_digest_enabled: v })} testid="daily-digest-toggle" />
        </div>
        <div className={`rounded-lg border px-3 py-2 text-xs flex items-center gap-2 ${digestTpl && digestTpl.status === "APPROVED" ? "border-green-500/30 bg-green-50 text-green-700" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
          {digestTpl === null ? <span>Checking template…</span>
            : digestTpl === false || !digestTpl ? <span><b>Template not created yet.</b> Go to Settings → Templates and click "Create &amp; submit" to create the daily digest template, then approve it.</span>
            : digestTpl.status === "APPROVED" ? <span><b>Template approved ✅</b> Daily digests will be delivered reliably.</span>
            : <span><b>Template status: {digestTpl.status || "In approval"}.</b> Digests start once Meta approves it (Settings → Templates → Sync).</span>}
        </div>
        <div className="flex items-center gap-2 text-xs text-[#54656F]">
          <span>Send time:</span>
          <span className="font-mono px-2 py-1 rounded-md border border-[#E9EDEF] bg-[#F9FAFB]">9:00 AM IST (daily)</span>
        </div>
        <p className="text-[10px] text-[#8696A0]">Only sends on days you have new leads to follow up. Never sends to your own business number.</p>
      </div>
    </div>
  );
}

export default function Settings() {  const { tab } = useParams();
  const navigate = useNavigate();
  const active = tab || "whatsapp";

  return (
    <>
      <PageHeader eyebrow="Configuration" title="Settings" />
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex gap-1 px-4 md:px-8 pt-4 border-b border-[#E9EDEF] bg-[#F0F2F5] flex-wrap">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`settings-tab-${t.k}`} onClick={() => navigate(`/settings/${t.k}`)}
              className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${active === t.k ? "border-[#25D366] text-[#111B21]" : "border-transparent text-[#8696A0] hover:text-[#374151]"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="p-4 md:p-8">
          {active === "business" && <BusinessTab />}
          {active === "whatsapp" && <WhatsAppSettings />}
          {active === "wa-profile" && <WhatsAppProfileTab />}
          {active === "templates" && <TemplatesTab />}
          {active === "pricing" && <PricingSettings />}
          {active === "notifications" && <NotificationsTab />}
          {active === "ai" && (
            <div className="border border-[#E9EDEF] bg-white rounded-lg p-8 text-center space-y-4 max-w-md mx-auto">
              <Bot className="h-8 w-8 text-[#075E54] mx-auto" />
              <p className="text-sm text-[#54656F]">Configure the MuviArts.Online AI Assistant — persona, tone, model and instructions — on the dedicated AI Agent page.</p>
              <button data-testid="goto-ai-agent" onClick={() => navigate("/ai-agent")}
                className="inline-flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors">
                Open AI Agent <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
          {active === "voice" && <VoiceConnections />}
          {active === "pipeline" && <PipelineTab />}
          {active === "security" && <SecurityTab />}
        </div>
      </div>
    </>
  );
}
